import React from 'react';
import { Star } from 'lucide-react';

interface Testimonial {
  id: number;
  name: string;
  location: string;
  image: string;
  text: string;
  rating: number;
  model: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Michael Chen',
    location: 'San Francisco',
    image: 'https://images.pexels.com/photos/220453/pexels-photo-220453.jpeg?auto=compress&cs=tinysrgb&w=600',
    text: 'Since getting my City Glider, my daily commute has transformed from a stressful drive to an enjoyable ride. The battery lasts exactly as promised, and I love how I can weave through traffic effortlessly.',
    rating: 5,
    model: 'City Glider'
  },
  {
    id: 2,
    name: 'Sarah Johnson',
    location: 'Denver',
    image: 'https://images.pexels.com/photos/415829/pexels-photo-415829.jpeg?auto=compress&cs=tinysrgb&w=600',
    text: 'The Mountain Explorer has completely changed my weekend adventures. I can explore trails I never thought possible on a bike. The motor assistance on steep climbs is fantastic, and the build quality is exceptional.',
    rating: 5,
    model: 'Mountain Explorer'
  },
  {
    id: 3,
    name: 'David Rodriguez',
    location: 'New York City',
    image: 'https://images.pexels.com/photos/614810/pexels-photo-614810.jpeg?auto=compress&cs=tinysrgb&w=600',
    text: 'As someone who uses multiple forms of transport daily, the Fold & Go is a game-changer. I can take it on the subway, fold it under my desk at work, and it fits perfectly in my apartment. Incredibly convenient!',
    rating: 4,
    model: 'Fold & Go'
  }
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  return (
    <div className="bg-white rounded-lg shadow-lg p-6 transition-all duration-300 hover:shadow-xl">
      <div className="flex items-center mb-4">
        <img 
          src={testimonial.image} 
          alt={testimonial.name} 
          className="w-12 h-12 rounded-full object-cover mr-4"
        />
        <div>
          <h3 className="font-bold text-gray-800">{testimonial.name}</h3>
          <p className="text-gray-500 text-sm">{testimonial.location}</p>
        </div>
      </div>
      
      <div className="flex mb-3">
        {[...Array(5)].map((_, i) => (
          <Star 
            key={i}
            size={16}
            className={i < testimonial.rating ? "text-yellow-400 fill-yellow-400" : "text-gray-300"}
          />
        ))}
      </div>
      
      <p className="text-gray-600 italic mb-4">"{testimonial.text}"</p>
      
      <p className="text-primary text-sm font-medium">Model: {testimonial.model}</p>
    </div>
  );
};

const AwardBadge: React.FC<{ name: string; year: string }> = ({ name, year }) => {
  return (
    <div className="flex flex-col items-center p-4">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-2">
        <Star size={24} className="text-primary fill-primary" />
      </div>
      <h3 className="font-bold text-center text-gray-800">{name}</h3>
      <p className="text-gray-500 text-sm">{year}</p>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section id="testimonials" className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">What Our Riders Say</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Hear from our community of riders about their experiences with ECORIDE electric bicycles.
          </p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
          {testimonials.map(testimonial => (
            <TestimonialCard key={testimonial.id} testimonial={testimonial} />
          ))}
        </div>
        
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-2xl font-bold text-gray-800 text-center mb-8">Recognition & Awards</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 justify-items-center">
            <AwardBadge name="Best E-Bike Design" year="2023" />
            <AwardBadge name="Mobility Innovation Award" year="2023" />
            <AwardBadge name="Eco-Transportation Excellence" year="2022" />
            <AwardBadge name="Consumer Choice Award" year="2022" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;